import { useState, useMemo, useEffect, useCallback } from 'react';
import { SIZE_OPTIONS, LENGTH_OPTIONS } from '../constants/serviceOptions';
import type { PricingCombination } from '../types/appointments';
import type {
	PublicBookingAddOn,
	PublicBookingService,
	SelectedAddOn,
} from '../types/publicBooking';

/** An add-on is either off the basket or on it at least once. */
export const ADD_ON_MIN_QUANTITY = 1;

/** Ceiling the API enforces per add-on; the stepper stops here too. */
export const ADD_ON_MAX_QUANTITY = 10;

/** Services that come back with no category are grouped under this heading. */
export const OTHERS_CATEGORY = 'Others';

/** Pseudo-category for the "show everything" filter pill. */
export const ALL_CATEGORIES = 'All';

export interface ServiceCostLine {
	id: number;
	name: string;
	price: number;
	durationMinutes: number;
}

const categoryOf = (service: PublicBookingService): string =>
	service.category?.trim() || OTHERS_CATEGORY;

/**
 * Orders a set of size/length codes by the canonical catalogue order so the
 * pills always read S → M → L rather than in whatever order the API returned
 * the pricing rows. Codes outside the catalogue keep their relative order and
 * are appended.
 */
const orderByCatalogue = (values: Set<string>, catalogue: string[]): string[] => {
	const known = catalogue.filter(v => values.has(v));
	const unknown = [...values].filter(v => !catalogue.includes(v));
	return [...known, ...unknown];
};

/** Reads a pricing code: null, undefined and whitespace all mean "not priced by this". */
const codeOf = (value: string | null | undefined): string => (value ?? '').trim();

/**
 * Whether a pricing row applies to what the customer picked. A blank code on
 * the row is a wildcard — an unsized service prices every size — and a blank
 * pick matches every row. Used for narrowing the option lists and for the
 * last-resort estimate in `resolveCombination`, not for pricing a normal
 * booking: the ladder below is stricter and is what the server does.
 */
const appliesTo = (rowCode: string, picked: string): boolean =>
	!rowCode || !picked || rowCode === picked;

/** Lowest-priced row of a set, or null for an empty one. */
const cheapest = (rows: PricingCombination[]): PricingCombination | null =>
	rows.reduce<PricingCombination | null>(
		(best, row) => (best === null || row.price < best.price ? row : best),
		null
	);

/** The sizes a service is actually priced at. Empty means the service isn't sized. */
const sizesOf = (service: PublicBookingService): Set<string> =>
	new Set(service.pricingCombinations.map(pc => codeOf(pc.size)).filter(Boolean));

/** As `sizesOf`, for lengths. */
const lengthsOf = (service: PublicBookingService): Set<string> =>
	new Set(service.pricingCombinations.map(pc => codeOf(pc.length)).filter(Boolean));

/**
 * The row a service is priced by, most specific first: the exact size/length
 * pair, then the size on its own, then the length on its own, then the
 * catch-all row that prices everything. Price and duration both come from
 * whichever row wins.
 *
 * This mirrors the server's resolver, verified against the API on 2026-09-27
 * with a service carrying all four row shapes. Two properties are worth
 * spelling out because they are not obvious and a cheaper-looking row makes
 * them easy to get wrong:
 *
 *  - an exact row wins even when a wildcard row is cheaper (SM + BRA resolved
 *    to the $40 exact row, not the $10 catch-all);
 *  - size outranks length, so a pair with no exact row falls back to the size
 *    row rather than the length row (XXS + BRA resolved to XXS/– at $50, not
 *    –/BRA at $30).
 *
 * A field the customer was never asked for is blank here, which lines up with
 * the null the server sees: the steps naming it then only match rows that are
 * themselves blank, exactly as the server's do.
 */
const resolveCombination = (
	service: PublicBookingService,
	size: string,
	length: string
): PricingCombination | null => {
	const rows = service.pricingCombinations;
	const pricedAt = (wantSize: string, wantLength: string) =>
		cheapest(
			rows.filter(pc => codeOf(pc.size) === wantSize && codeOf(pc.length) === wantLength)
		);

	return (
		pricedAt(size, length) ??
		pricedAt(size, '') ??
		pricedAt('', length) ??
		pricedAt('', '') ??
		// Nothing on the ladder fits only when a field the service prices by was
		// left unset — the basket whose services share no size, where there is
		// nothing for the customer to pick. Quote the cheapest row it could turn
		// out to be rather than showing nothing; the page says the stylist will
		// confirm, and the server's own figures replace this once the booking is
		// created.
		cheapest(
			rows.filter(
				pc => appliesTo(codeOf(pc.size), size) && appliesTo(codeOf(pc.length), length)
			)
		)
	);
};

/** Intersects the per-service option sets, ordered by the canonical catalogue. */
const sharedOptions = (perService: Set<string>[], catalogue: string[]): string[] => {
	if (perService.length === 0) return [];
	const shared = new Set(
		[...perService[0]].filter(candidate => perService.every(set => set.has(candidate)))
	);
	return orderByCatalogue(shared, catalogue);
};

/**
 * Drives the service → size / length selection shared by both public booking
 * pages.
 *
 * Not every service is priced by size and length: a consultation or a wash
 * carries a single combination with both left blank, which is why the create
 * API takes the fields as optional. So the two questions are asked only when
 * something in the basket is priced by them, and answered whenever they are
 * asked. That, plus `resolveCombination` walking the same ladder the server
 * walks, is what keeps the total quoted here identical to the one charged.
 *
 * The API takes many `serviceIds` but a single `size` and `length`, so the
 * options offered are the ones every service that prices by that field can
 * actually be booked at (the intersection of their pricing combinations).
 */
export const usePublicBookingSelection = (
	services: PublicBookingService[],
	addOns: PublicBookingAddOn[] = []
) => {
	const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	// An array rather than an id→quantity map so the basket keeps the order the
	// customer built it in — object keys that look like numbers would re-sort
	// themselves numerically and shuffle the summary lines.
	const [addOnLines, setAddOnLines] = useState<{ id: number; quantity: number }[]>([]);
	const [size, setSize] = useState('');
	const [length, setLength] = useState('');

	// ── Categories ──
	const categories = useMemo(() => {
		const seen: string[] = [];
		services.forEach(s => {
			const cat = categoryOf(s);
			if (!seen.includes(cat)) seen.push(cat);
		});
		// "Others" always sits last, however the API ordered the services.
		const ordered = seen.filter(c => c !== OTHERS_CATEGORY);
		if (seen.includes(OTHERS_CATEGORY)) ordered.push(OTHERS_CATEGORY);
		return [ALL_CATEGORIES, ...ordered];
	}, [services]);

	const selectedServices = useMemo(
		() => selectedIds.map(id => services.find(s => s.id === id)).filter(Boolean),
		[selectedIds, services]
	) as PublicBookingService[];

	// ── Size / length options ──
	// Only services actually priced by a field get a say in its options: a wash
	// sharing the basket with braids must not empty the size pills just because
	// the wash has no sizes of its own.
	const sizedServices = useMemo(
		() => selectedServices.filter(service => sizesOf(service).size > 0),
		[selectedServices]
	);

	const lengthedServices = useMemo(
		() => selectedServices.filter(service => lengthsOf(service).size > 0),
		[selectedServices]
	);

	const sizeOptions = useMemo(
		() => sharedOptions(sizedServices.map(sizesOf), SIZE_OPTIONS),
		[sizedServices]
	);

	// Narrowed to the chosen size, since a service need not offer every length at
	// every size. A service left with nothing at that size keeps its empty set
	// here rather than dropping out — that clash has to reach the customer.
	const lengthOptions = useMemo(
		() =>
			sharedOptions(
				lengthedServices.map(
					service =>
						new Set(
							service.pricingCombinations
								.filter(pc => appliesTo(codeOf(pc.size), size))
								.map(pc => codeOf(pc.length))
								.filter(Boolean)
						)
				),
				LENGTH_OPTIONS
			),
		[lengthedServices, size]
	);

	// Adding or removing a service can invalidate an earlier pick — drop it
	// rather than silently submitting a size the new service isn't priced for.
	useEffect(() => {
		if (size && !sizeOptions.includes(size)) setSize('');
	}, [size, sizeOptions]);

	useEffect(() => {
		if (length && !lengthOptions.includes(length)) setLength('');
	}, [length, lengthOptions]);

	// ── Pricing ──
	const costLines = useMemo<ServiceCostLine[]>(() => {
		return selectedServices.map(service => {
			const combo = resolveCombination(service, size, length);
			return {
				id: service.id,
				name: service.name,
				price: combo?.price ?? 0,
				durationMinutes: combo?.durationMinutes ?? 0,
			};
		});
	}, [selectedServices, size, length]);

	const serviceOnlyTotal = useMemo(
		() => costLines.reduce((total, line) => total + line.price, 0),
		[costLines]
	);

	// ── Add-ons ──
	const selectedAddOns = useMemo(
		() =>
			addOnLines
				.map(line => {
					const addOn = addOns.find(a => a.id === line.id);
					if (!addOn) return null;
					return {
						...addOn,
						quantity: line.quantity,
						lineTotal: (addOn.price ?? 0) * line.quantity,
					};
				})
				.filter(Boolean),
		[addOnLines, addOns]
	) as SelectedAddOn[];

	const selectedAddOnIds = useMemo(() => selectedAddOns.map(addOn => addOn.id), [selectedAddOns]);

	/** The payload shape: `{ [addOnId]: quantity }`. */
	const addOnQuantities = useMemo(
		() =>
			selectedAddOns.reduce<Record<number, number>>((map, addOn) => {
				map[addOn.id] = addOn.quantity;
				return map;
			}, {}),
		[selectedAddOns]
	);

	const addOnTotal = useMemo(
		() => selectedAddOns.reduce((total, addOn) => total + addOn.lineTotal, 0),
		[selectedAddOns]
	);

	/**
	 * Everything the deposit and support fee are banded on. Add-ons are part of
	 * what the customer is quoted, so they have to count towards the total the
	 * fee tiers are read with — otherwise a $90 service plus a $40 add-on would
	 * be charged the deposit for a $90 booking.
	 */
	const serviceTotal = serviceOnlyTotal + addOnTotal;

	/**
	 * How long the whole basket takes, add-ons included. The availability call
	 * sends this so the server can drop slots the job would overrun — a two-hour
	 * install used to be offered half an hour before closing.
	 */
	const totalDurationMinutes = useMemo(
		() =>
			costLines.reduce((total, line) => total + line.durationMinutes, 0) +
			selectedAddOns.reduce(
				(total, addOn) => total + (addOn.durationMinutes ?? 0) * addOn.quantity,
				0
			),
		[costLines, selectedAddOns]
	);

	/** Selecting an add-on starts it at one; deselecting forgets its quantity. */
	const toggleAddOn = useCallback((id: number) => {
		setAddOnLines(prev =>
			prev.some(line => line.id === id)
				? prev.filter(line => line.id !== id)
				: [...prev, { id, quantity: ADD_ON_MIN_QUANTITY }]
		);
	}, []);

	// Clamped here rather than only in the stepper so the bounds hold however
	// the quantity is set — the payload can never carry an 11 or a 0.
	const setAddOnQuantity = useCallback((id: number, quantity: number) => {
		const clamped = Math.min(
			ADD_ON_MAX_QUANTITY,
			Math.max(ADD_ON_MIN_QUANTITY, Math.round(quantity))
		);
		setAddOnLines(prev =>
			prev.map(line => (line.id === id ? { ...line, quantity: clamped } : line))
		);
	}, []);

	const clearAddOns = useCallback(() => setAddOnLines([]), []);

	// ── Actions ──
	const toggleService = useCallback((id: number) => {
		setSelectedIds(prev =>
			prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
		);
	}, []);

	const removeService = useCallback((id: number) => {
		setSelectedIds(prev => prev.filter(existing => existing !== id));
	}, []);

	/** Whether anything in the basket is priced by the field. If not, don't ask. */
	const sizeApplies = sizedServices.length > 0;
	const lengthApplies = lengthedServices.length > 0;

	/** Asked but unanswerable: the sized services share no size at all. */
	const hasNoSharedSize = sizeApplies && sizeOptions.length === 0;

	/**
	 * Sharing a size does not guarantee sharing a length at that size: one
	 * service may only be priced M/SHOULDER and another only M/WAIST.
	 */
	const hasNoSharedLength = lengthApplies && lengthOptions.length === 0;

	/**
	 * A pick the customer has to make before the total is settled. A clash
	 * leaves nothing to pick, so that booking goes through unset and is priced
	 * from whatever the stylist confirms.
	 */
	const requiresSize = sizeApplies && !hasNoSharedSize;
	const requiresLength = lengthApplies && !hasNoSharedLength;

	return {
		categories,
		activeCategory,
		setActiveCategory,
		selectedIds,
		selectedServices,
		toggleService,
		removeService,
		size,
		setSize,
		length,
		setLength,
		sizeOptions,
		lengthOptions,
		sizeApplies,
		lengthApplies,
		requiresSize,
		requiresLength,
		hasNoSharedSize,
		hasNoSharedLength,
		costLines,
		selectedAddOnIds,
		selectedAddOns,
		addOnQuantities,
		toggleAddOn,
		setAddOnQuantity,
		clearAddOns,
		addOnTotal,
		serviceOnlyTotal,
		serviceTotal,
		totalDurationMinutes,
	};
};

export default usePublicBookingSelection;
